import React, { useState, useEffect } from 'react';
import { paraSlug } from '@nyroh/contracts';
import { createRoot } from 'react-dom/client';
import { Layout, Plus, Loader2, Database, Settings, Factory, CheckCircle, Square, Globe, Brain, Coins, Sparkles } from 'lucide-react';

// Importing Types
import { Client, GlobalSettings, ThemeConfig, QueueItem } from './types';

// Importing Services
import {
    isSupabaseConfigured,
    listarClientes,
    obterClienteCompleto,
    salvarCliente,
    excluirCliente,
    excluirClientes,
    verificarDuplicados,
    existeTelefone,
    entrarComoCliente,
} from './lib/dados';
import { auth } from './lib/api';
import { generateSiteContent } from './lib/gemini';
import { IaControlView } from './components/IaControlView';

// Importing Components
import { DashboardView } from './components/DashboardView';
import { RegistryView } from './components/RegistryView';
import { SettingsView } from './components/SettingsView';
import { ClientDetailOverlay } from './components/ClientDetailOverlay';
import { PublicGateway } from './components/PublicGateway';
import { MassGeneratorView } from './components/MassGeneratorView';
import { AgentIntelligenceView } from './components/AgentIntelligenceView';
import { VisualEditor } from './components/VisualEditor';
import { EditorDeTemplate } from './components/EditorDeTemplate';
import { LoginScreen } from './components/LoginScreen';
import { ClientPortalView } from './components/ClientPortalView'; // NEW
import { CostDashboardView } from './components/CostDashboardView';
import ExternalLeads from './ExternalLeads';

// --- Initial Data / Utils ---

const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Slug pela regra do CONTRATO, que e a mesma que o `slugSchema` valida.
 *
 * A versao local tinha dois defeitos que so aparecem no envio: nao limitava o
 * tamanho (o schema recusa acima de 120 caracteres) e aceitava `_`, porque usava
 * `\w` — e o regex do schema so aceita `[a-z0-9]` separado por hifen. Nos dois
 * casos o lote inteiro era recusado pelo cliente HTTP antes de sair.
 */
const generateSlug = (text: string) => paraSlug(text || 'projeto-novo') || 'novo-projeto';

const DEFAULT_SETTINGS: GlobalSettings = {
    defaultTrialValue: 7,
    defaultTrialUnit: 'days'
};

interface SidebarItemProps {
    icon: React.ReactNode;
    label: string;
    active: boolean;
    onClick: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon, label, active, onClick }) => (
    <button 
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 group border-l-2 ${
            active 
            ? 'bg-slate-100 text-slate-900 border-orange-700' 
            : 'text-slate-500 hover:bg-zinc-50 hover:text-slate-700 border-transparent'
        }`}
    >
        <div className={`transition-colors ${active ? 'text-orange-700' : 'text-slate-400 group-hover:text-slate-600'}`}>
            {icon}
        </div>
        <span className="font-semibold text-sm tracking-tight">{label}</span>
    </button>
);

const App = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [view, setView] = useState<'dashboard' | 'registry' | 'settings' | 'factory' | 'external-leads' | 'intelligence' | 'costs' | 'ia'>('dashboard');
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [clientInEditor, setClientInEditor] = useState<Client | null>(null); 
    /**
     * O cliente aberto no EDITOR DE TEMPLATE (o caminho sem IA).
     *
     * Separado de `clientInEditor` de propósito: aquele edita o HTML que já
     * existe, com o copiloto; este preenche os campos de um template e publica
     * um spec tipado. São dois caminhos diferentes para dois estados diferentes
     * do cliente, e misturá-los faria um sobrescrever o outro.
     */
    const [clienteNoTemplate, setClienteNoTemplate] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [isFetchingFull, setIsFetchingFull] = useState(false);
    
    const [factoryQueue, setFactoryQueue] = useState<QueueItem[]>([]);
    const [factorySelectedIds, setFactorySelectedIds] = useState<Set<string>>(new Set());
    
    // --- AUTH STATE ---
    const [authRole, setAuthRole] = useState<'admin' | 'client' | null>(null);
    const [loggedInClient, setLoggedInClient] = useState<Client | null>(null); // For client portal
    
    const [globalSettings, setGlobalSettings] = useState<GlobalSettings>(() => {
        try {
            const saved = localStorage.getItem('genwebos_settings');
            return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
        } catch (e) { return DEFAULT_SETTINGS; }
    });
    
    const [publicModeClient, setPublicModeClient] = useState<Client | null>(null);
    const [editorModeClient, setEditorModeClient] = useState<Client | null>(null);
    const [isCheckingRoute, setIsCheckingRoute] = useState(true);

    useEffect(() => {
        const init = async () => {
            setLoadingData(true);
            
            // Initial fetch: only today's records
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            try {
                const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 8000));
                await Promise.race([fetchClients(today.getTime()), timeoutPromise]);
            } catch (err) {
                console.log("Timeout ou Erro Inicial. Usando backup local.");
                
                try {
                    const localBackup = localStorage.getItem('ditho_backup_clients');
                    if (localBackup) {
                         const parsedBackup = JSON.parse(localBackup);
                         if (Array.isArray(parsedBackup)) {
                             const safeBackup = parsedBackup
                                .filter(c => c && typeof c === 'object')
                                .map((c: any) => ({
                                    ...c,
                                    id: c.id || generateId(),
                                    name: c.name || "Projeto Recuperado (Local)",
                                    industry: c.industry || "Geral",
                                    status: c.status || 'draft',
                                    slug: c.slug || generateSlug(c.name || 'recuperado')
                                }));
                             setClients(safeBackup);
                         }
                    }
                } catch (e) {
                    console.error("Backup local corrompido.", e);
                }
            }
            
            setLoadingData(false);
        };
        init();
        
        try {
            // A sessão do operador é lida do servidor, não de localStorage.
            void auth.getSession({ query: {} }).then((r) => { if (r.data) setAuthRole("admin"); });
        } catch (e) {}
    }, []);;

    useEffect(() => {
        if (!loadingData) {
            const params = new URLSearchParams(window.location.search);
            const siteParam = params.get('site');
            const editorParam = params.get('editor');
            if (siteParam) {
                const foundClient = clients.find(c => c.id === siteParam || c.slug === siteParam);
                if (foundClient) setPublicModeClient(foundClient);
            } else if (editorParam) {
                const foundClient = clients.find(c => c.id === editorParam || c.slug === editorParam);
                if (foundClient) setEditorModeClient(foundClient);
            }
            setIsCheckingRoute(false);
        }
    }, [loadingData, clients]);

    useEffect(() => {
        // Removido backup local de clientes pois excede quota de 5MB para grandes volumes.
        // O Supabase é nossa única fonte de verdade agora.
    }, [clients]);

    const fetchClients = async (fromTimestamp?: number, toTimestamp?: number, onlyMetadata: boolean = true) => {
        if (isSupabaseConfigured) {
            setIsFetching(true);
            try {
                // ============================================================
                // A CONSULTA QUE BAIXAVA AS SENHAS SAIU DAQUI
                // ============================================================
                //
                // O que estava escrito antes, literalmente:
                //
                //     'id, name, slug, ..., email, password'
                //
                // A cada carregamento, o app selecionava explicitamente `email` e
                // `password` de todos os clientes do período e baixava para o
                // navegador. A coluna era texto puro, com default '123', e o
                // login comparava em memória. Qualquer pessoa com o DevTools
                // aberto via a credencial de todos os clientes. É a falha S-10.
                //
                // Agora a listagem vem da API, que não tem essa coluna para
                // devolver: o PIN virou hash argon2id em tabela separada,
                // verificado só no servidor.
                //
                // `onlyMetadata` também perdeu a razão de existir: `site_content`
                // não mora mais no banco. Está mantido na assinatura para não
                // mexer nos pontos de chamada.
                void onlyMetadata;

                const linhas = await listarClientes({
                    de: fromTimestamp,
                    ate: toTimestamp,
                    limite: 200,
                });

                if (linhas) {
                    const mappedClients: Client[] = linhas.map((row) => ({
                        id: row.id,
                        name: row.name || "Projeto Sem Nome",
                        slug: row.slug || generateSlug(row.name || "projeto"),
                        industry: row.industry || 'Geral',
                        scope: row.scope || '',
                        status: (row.status as any) || 'draft',
                        paymentStatus: (row.payment_status as any) || 'pending',
                        // O HTML vem do R2 quando alguém abre o projeto, não na
                        // listagem. Foi guardar HTML no banco que estourou a cota
                        // do banco anterior desta aplicação.
                        siteContent: '',
                        subdomain: row.subdomain,
                        createdAt: row.created_at ?? Date.now(),
                        trialHours: row.trial_hours || (DEFAULT_SETTINGS.defaultTrialValue * 24),
                        paymentLink: row.payment_link,
                        leadData: row.lead_data,
                        themeConfig: row.theme_config,
                        email: row.email,
                        // Sempre `undefined`. Não existe mais senha para trazer.
                        password: undefined,
                    })) as Client[];

                    if (fromTimestamp || toTimestamp) {
                        setClients(prev => {
                            const otherClients = prev.filter(c => {
                                if (fromTimestamp && c.createdAt < fromTimestamp) return true;
                                if (toTimestamp && c.createdAt > toTimestamp) return true;
                                return false;
                            });
                            return [...mappedClients, ...otherClients].sort((a,b) => b.createdAt - a.createdAt);
                        });
                    } else {
                        setClients(mappedClients);
                    }
                }
            } catch (err) {
                console.error("Erro ao buscar do Supabase.");
            } finally {
                setIsFetching(false);
            }
        }
    };

    const fetchFullClientData = async (id: string): Promise<Client | null> => {
        if (!isSupabaseConfigured) return null;
        setIsFetchingFull(true);
        try {
            // O HTML vem do R2; o resto, do Postgres.
            //
            // A consulta antiga era `select('*')` e trazia `site_content` inteiro
            // do banco a cada abertura de projeto — a mesma coluna que fez o banco
            // anterior desta aplicação ser abandonado por estourar cota.
            //
            // `password` também saía nessa consulta, em texto puro (falha S-10).
            // Não sai mais: a credencial virou hash argon2id em tabela própria.
            const data = await obterClienteCompleto(id);
            if (data) {
                const fullClient: Client = {
                    id: data.id,
                    name: data.name || "Projeto Sem Nome",
                    slug: data.slug || generateSlug(data.name || "projeto"),
                    industry: data.industry || 'Geral',
                    scope: data.scope || '',
                    status: (data.status as any) || 'draft',
                    paymentStatus: (data.payment_status as any) || 'pending',
                    siteContent: data.site_content || '',
                    subdomain: data.subdomain,
                    createdAt: data.created_at ?? Date.now(),
                    trialHours: data.trial_hours || (DEFAULT_SETTINGS.defaultTrialValue * 24),
                    paymentLink: data.payment_link,
                    leadData: data.lead_data,
                    themeConfig: data.theme_config,
                    email: data.email,
                    password: undefined,
                } as Client;

                setClients(prev => prev.map(c => c.id === id ? fullClient : c));
                return fullClient;
            }
            return null;
        } catch (err) {
            console.error("Erro ao buscar detalhes do cliente:", err);
            return null;
        } finally {
            setIsFetchingFull(false);
        }
    };

    const checkDuplicate = async (phoneNumber: string): Promise<boolean> => {
        if (!isSupabaseConfigured) return false;
        try {
            // Antes: `.eq('lead_data->>phoneNumber', ...)` — busca dentro do JSONB
            // sem índice, exatamente o padrão que causava statement timeout no
            // painel. O telefone agora é COLUNA, e a coluna tem índice.
            return await existeTelefone(phoneNumber);
        } catch (err) {
            console.error("Erro ao verificar duplicata:", err);
            return false;
        }
    };

    const checkDuplicatesBulk = async (items: { name: string, phone: string }[]): Promise<{ name: boolean, phone: boolean, id: string }[]> => {
        if (!isSupabaseConfigured || items.length === 0) return [];
        try {
            // A versão antiga montava o filtro PostgREST concatenando nomes dentro
            // de aspas: `name.in.("Fulano","Beltrano")`. Um nome com aspas ou
            // vírgula quebrava a consulta, e um nome contendo sintaxe de filtro
            // podia mudar o significado dela. Aqui os termos vão como PARÂMETRO e
            // quem monta o SQL é o Prisma.
            return await verificarDuplicados(items);
        } catch (err) {
            console.error("Erro no check bulk:", err);
            return [];
        }
    };

    const persistClient = async (client: Client) => {
        if (!isSupabaseConfigured) return;
        try {
            // Duas mudanças de fundo neste bloco:
            //
            // 1. `password` sumiu do payload. A credencial do cliente é criada
            //    pelo servidor, com PIN de CSPRNG e hash argon2id, e nunca volta.
            //
            // 2. `site_content` não vai mais para o banco. O HTML é publicado
            //    como artefato no R2 e o banco guarda o ponteiro e o hash;
            //    republicar conteúdo idêntico não escreve nada.
            await salvarCliente({
                id: client.id,
                name: client.name,
                slug: client.slug || generateSlug(client.name),
                industry: client.industry,
                scope: client.scope,
                status: client.status,
                payment_status: client.paymentStatus,
                subdomain: client.subdomain,
                created_at: client.createdAt,
                trial_hours: client.trialHours,
                payment_link: client.paymentLink,
                email: client.email,
                site_content: client.siteContent,
            });
        } catch (err) {
            console.error("Erro ao salvar cliente:", err);
            alert(err instanceof Error ? err.message : 'Erro ao salvar o cliente.');
        }
    };

    const deleteClient = async (id: string) => {
        try {
            // A mensagem antiga mandava o operador "verificar se o RLS permite
            // DELETE" — instrução que só fazia sentido porque o navegador falava
            // direto com o banco. Agora a API responde 403 quando o cargo não
            // permite, e a mensagem vem dela.
            const removidos = await excluirCliente(id);
            if (removidos === 0) {
                alert("O registro não foi encontrado. Talvez já tenha sido excluído.");
                return;
            }
            setClients(prev => prev.filter(c => c.id !== id));
            if (selectedClient?.id === id) setSelectedClient(null);
        } catch (err) {
            console.error("Erro ao excluir cliente:", err);
            alert(err instanceof Error ? err.message : "Erro ao excluir. Tente novamente.");
        }
    };

    const deleteMultipleClients = async (ids: string[]) => {
        try {
            const removidos = await excluirClientes(ids);
            if (removidos === 0) {
                alert("Nenhum registro foi removido. Verifique se você tem permissão para excluir.");
                return;
            }
            const idSet = new Set(ids);
            setClients(prev => prev.filter(c => !idSet.has(c.id)));
            if (selectedClient && idSet.has(selectedClient.id)) setSelectedClient(null);
            alert(`Sucesso! ${removidos} registros foram removidos permanentemente.`);
        } catch (err) {
            console.error("Erro na exclusão em massa:", err);
            alert(err instanceof Error ? err.message : "Erro ao excluir em massa.");
        }
    };

    const addClient = () => {
        const multiplier = globalSettings.defaultTrialUnit === 'days' ? 24 : 1;
        const calculatedHours = globalSettings.defaultTrialValue * multiplier;
        const name = "Novo Cliente";
        const newId = generateId();
        const newClient: Client = {
            id: newId,
            name: name,
            slug: generateSlug(name) + '-' + newId.substring(0,3),
            industry: "",
            scope: "",
            status: 'draft',
            paymentStatus: 'pending',
            createdAt: Date.now(),
            trialHours: calculatedHours,
            // Default Auth
            email: `cliente-${newId.substring(0,4)}@ditho.com`,
            password: '123'
        };
        setClients(prev => [newClient, ...prev]);
        setSelectedClient(newClient);
        persistClient(newClient);
    };

    const handleFactoryClients = (newClients: Client[]) => {
        const processedClients = newClients.map(c => ({
            ...c,
            slug: c.slug || (generateSlug(c.name) + '-' + generateId().substring(0,3)),
            // Generate auth for factory clients
            email: c.email || `cliente-${generateId().substring(0,4)}@ditho.com`,
            password: c.password || '123'
        }));
        setClients(prev => {
            const newIds = new Set(processedClients.map(c => c.id));
            const prevFiltered = prev.filter(c => !newIds.has(c.id));
            return [...processedClients, ...prevFiltered];
        });
        processedClients.forEach(c => persistClient(c));
    };

    const updateClient = (id: string, data: Partial<Client>) => {
        let updatedClient: Client | null = null;
        setClients(prev => prev.map(c => {
            if (c.id === id) {
                updatedClient = { ...c, ...data };
                return updatedClient;
            }
            return c;
        }));
        if (selectedClient?.id === id) setSelectedClient(prev => prev ? { ...prev, ...data } : null);
        
        // Also update logged in client state if applicable
        if (loggedInClient?.id === id) setLoggedInClient(prev => prev ? { ...prev, ...data } : null);

        if (updatedClient) persistClient(updatedClient);
    };

    const handleSelectClient = async (client: Client) => {
        setSelectedClient(client);
        // Se o cliente não tem siteContent (e não é rascunho), provavelmente é apenas metadados
        // Buscamos os dados completos em background
        if (client.status !== 'draft' && !client.siteContent && !client.leadData) {
            const full = await fetchFullClientData(client.id);
            if (full) setSelectedClient(full);
        }
    };

    const generateSite = async (client: Client) => {
        // A guarda `if (!process.env.API_KEY) return;` saiu daqui.
        //
        // Ela dependia do `define` do Vite, que inlinava a chave no bundle. Com a
        // chave de volta ao servidor, `process.env` vira `{}` no navegador e a
        // guarda passaria a abortar SEMPRE, em silêncio — o botão não faria nada.
        // Quem sabe se há IA disponível é a API; sem ela, o template cobre.
        updateClient(client.id, { status: 'generating' });
        setIsGenerating(true);
        try {
            const html = await generateSiteContent(client);
            updateClient(client.id, { status: 'generated', siteContent: html });
        } catch (error) {
            updateClient(client.id, { status: 'draft' });
        } finally {
            setIsGenerating(false);
        }
    };

    const approveSite = (client: Client) => {
        let host = window.location.hostname;
        if (host.startsWith('www.')) host = host.substring(4);
        const finalSlug = client.slug || (generateSlug(client.name) + '-' + generateId().substring(0,3));
        const subdomain = `${finalSlug}.${host}`;
        updateClient(client.id, { status: 'approved', subdomain: subdomain, slug: finalSlug });
    };

    const togglePayment = (client: Client, e: React.MouseEvent) => {
        e.stopPropagation();
        const newStatus = client.paymentStatus === 'paid' ? 'pending' : 'paid';
        updateClient(client.id, { paymentStatus: newStatus });
    };

    // --- AUTH HANDLER ---
    // O login virou assíncrono: a comparação acontece no SERVIDOR.
    const handleLogin = async (
        role: 'admin' | 'client',
        email?: string,
        password?: string,
    ): Promise<string | void> => {
        if (role === 'admin') {
            // A sessão de admin vem do Better Auth, em cookie httpOnly assinado —
            // a MESMA conta e a mesma tabela do painel. O `email`/`password` agora
            // chegam preenchidos: a tela pedia só uma "access key" comparada como
            // string no navegador e chamava isto com os dois campos vazios, então
            // o Better Auth recusava e a fábrica só funcionava de carona no cookie
            // que o painel já tivesse emitido no mesmo domínio.
            const { error } = await auth.signIn.email({
                email: email ?? '',
                password: password ?? '',
            });
            if (error) {
                // Devolve em vez de `alert()`: a tela mostra no formulário, junto
                // do campo, e o fluxo não é interrompido por uma caixa do sistema.
                return error.message || 'E-mail ou senha incorretos.';
            }
            setAuthRole('admin');
            return;
        }

        if (role === 'client' && email && password) {
            // O QUE ISTO SUBSTITUI:
            //
            //   const foundClient = clients.find(
            //     c => c.email === email && c.password === password
            //   );
            //
            // A comparação acontecia na MEMÓRIA DO NAVEGADOR, contra uma lista de
            // e-mails e senhas em texto puro baixada a cada carregamento do app.
            const sessao = await entrarComoCliente(email, password);
            if (!sessao) {
                return 'Usuário ou senha incorretos.';
            }
            const cliente = clients.find(c => c.id === sessao.clienteId) ?? null;
            setLoggedInClient(cliente);
            setAuthRole('client');
        }
    };

    const handleLogout = () => {
        void auth.signOut({});
        setAuthRole(null);
        setLoggedInClient(null);
    };

    // --- RENDER LOGIC ---

    if (loadingData || isCheckingRoute) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-zinc-50 text-slate-600 gap-4">
                <Loader2 className="animate-spin text-orange-700" size={48} />
                <span className="text-sm font-bold tracking-widest uppercase text-slate-400">Ditho System v2.0</span>
            </div>
        );
    }

    // Public Views
    if (publicModeClient) return <PublicGateway client={publicModeClient} />;
    
    // Editor (Used by both Admin and Client)
    if (clienteNoTemplate) {
        return (
            <EditorDeTemplate
                clienteId={clienteNoTemplate}
                onFechar={() => setClienteNoTemplate(null)}
                // Recarrega só o cliente publicado: a lista inteira pode ter
                // milhares de linhas, e o que mudou foi um.
                onPublicado={() => { void fetchFullClientData(clienteNoTemplate); }}
            />
        );
    }

    if (clientInEditor) {
        return (
            <VisualEditor 
                client={clientInEditor} 
                onSave={(id, theme, html) => updateClient(id, { themeConfig: theme, siteContent: html })} 
                onBack={() => setClientInEditor(null)} 
            />
        );
    }

    // Auth Guard
    if (!authRole) return <LoginScreen onLogin={handleLogin} />;

    // CLIENT PORTAL VIEW
    if (authRole === 'client' && loggedInClient) {
        return (
            <ClientPortalView 
                client={loggedInClient} 
                onLogout={handleLogout} 
                onOpenEditor={(c) => setClientInEditor(c)}
                onUpdateClient={updateClient}
            />
        );
    }

    // ADMIN DASHBOARD VIEW (Original)
    return (
        <div className="flex h-screen overflow-hidden selection:bg-orange-700 selection:text-white font-sans text-slate-900 bg-zinc-50">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-zinc-200 flex flex-col z-20 shadow-sm">
                <div className="p-6 border-b border-zinc-100">
                    <div className="flex items-center gap-2 text-slate-900 font-bold text-xl tracking-tight">
                        <div className="w-5 h-5 bg-slate-900 rounded-sm"></div>
                        Ditho OS
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    <SidebarItem icon={<Layout size={18} />} label="Gestão de Projetos" active={view === 'dashboard'} onClick={() => setView('dashboard')} />
                    <SidebarItem icon={<Globe size={18} />} label="Portal de Leads" active={view === 'external-leads'} onClick={() => setView('external-leads')} />
                    <SidebarItem icon={<Factory size={18} />} label="Fábrica de Sites" active={view === 'factory'} onClick={() => setView('factory')} />
                    <SidebarItem icon={<Brain size={18} />} label="Cérebro do Agente" active={view === 'intelligence'} onClick={() => setView('intelligence')} />
                    <SidebarItem icon={<Database size={18} />} label="Banco de Registros" active={view === 'registry'} onClick={() => setView('registry')} />
                    <SidebarItem icon={<Sparkles size={18} />} label="Inteligência Artificial" active={view === 'ia'} onClick={() => setView('ia')} />
                    <SidebarItem icon={<Coins size={18} />} label="Gestão de Custos" active={view === 'costs'} onClick={() => setView('costs')} />
                    <SidebarItem icon={<Settings size={18} />} label="Configurações" active={view === 'settings'} onClick={() => setView('settings')} />
                </nav>
                <div className="p-4 border-t border-zinc-100 flex flex-col gap-2 text-xs text-slate-400 text-center font-mono">
                    <span>build v2.0.4 [stable]</span>
                    <button onClick={handleLogout} className="text-red-500 hover:text-red-700 hover:underline">Sair do Admin</button>
                </div>
            </aside>

            <main className="flex-1 flex flex-col relative bg-zinc-50">
                {/* Background Grid Pattern */}
                <div className="fixed top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none opacity-40"></div>
                          <header className="h-16 border-b border-zinc-200 flex items-center justify-between px-8 bg-white/80 backdrop-blur-md z-10 sticky top-0">
                    <h1 className="text-lg font-bold text-slate-800 tracking-tight flex items-center gap-2">
                        {view === 'dashboard' ? 'Centro de Comando' : view === 'factory' ? 'Fábrica' : view === 'registry' ? 'Registros' : view === 'external-leads' ? 'Leads Externos' : view === 'intelligence' ? 'Documentação da Inteligência' : view === 'costs' ? 'Auditoria de Custos' : view === 'ia' ? 'Inteligência Artificial' : 'Configurações'}
                    </h1>
                    {view === 'dashboard' && (
                        <button onClick={addClient} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 transition-all shadow-sm">
                            <Plus size={16} /> Novo Projeto
                        </button>
                    )}
                </header>

                <div className="flex-1 overflow-y-auto p-0">
                    {view === 'dashboard' ? (
                        <DashboardView clients={clients} onSelect={handleSelectClient} onDelete={deleteClient} onTogglePayment={togglePayment} />
                    ) : view === 'factory' ? (
                        <MassGeneratorView 
                            onAddClients={handleFactoryClients} 
                            globalSettings={globalSettings} 
                            onViewDetails={handleSelectClient} 
                            existingClients={clients} 
                            queue={factoryQueue} 
                            selectedIds={factorySelectedIds} 
                            onUpdateQueue={setFactoryQueue} 
                            onUpdateSelection={setFactorySelectedIds} 
                            onCheckDuplicatesBulk={checkDuplicatesBulk}
                        />
                    ) : view === 'registry' ? (
                        <div className="p-8 h-full">
                             <RegistryView 
                                 clients={clients.filter(c => c.status === 'approved')} 
                                 onDeleteMany={deleteMultipleClients}
                                 onFetchData={fetchClients}
                                 onFetchFullClientData={fetchFullClientData}
                                 isFetching={isFetching}
                             />
                        </div>
                    ) : view === 'external-leads' ? (
                        <ExternalLeads />
                    ) : view === 'intelligence' ? (
                        <AgentIntelligenceView />
                    ) : view === 'costs' ? (
                        <CostDashboardView clients={clients} />
                    ) : view === 'ia' ? (
                        <IaControlView />
                    ) : (
                        <div className="p-8"><SettingsView settings={globalSettings} onSave={setGlobalSettings} /></div>
                    )}
                </div>

                {selectedClient && (
                    <ClientDetailOverlay 
                        client={selectedClient} onClose={() => setSelectedClient(null)} onUpdate={updateClient}
                        onGenerate={() => generateSite(selectedClient)} onApprove={() => approveSite(selectedClient)}
                        isGenerating={isGenerating} isLoadingDetails={isFetchingFull} globalSettings={globalSettings} onOpenEditor={() => { setClientInEditor(selectedClient); setSelectedClient(null); }}
                        onOpenTemplate={() => { setClienteNoTemplate(selectedClient.id); setSelectedClient(null); }}
                    />
                )}
            </main>
        </div>
    );
};

const root = createRoot(document.getElementById('root')!);
root.render(<App />);